import React, { useEffect, useState } from "react";
import AppMenu from "../components/AppMenu";
import { Table } from "reactstrap";
import { getStateLogsById } from "../services/stateAPI";
import { getSiteById, getSiteData } from "../services/siteAPI";
import moment from "moment";
import { useParams } from "react-router";
import { title } from "../config";
import { BackButton } from "../components/BackButton";
import { useToast } from "../components/ToastContext";
import Pagination from "../components/Pagination";
import '../assets/css/State.css';

export function State() {
  const { showToast } = useToast();
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [logsPerPage, setLogsPerPage] = useState(100);
  const { id } = useParams();

  const StateLogsTable = ({ logs, currentPage, logsPerPage }) => (
    <Table className="table table-hover" responsive hover>
      <thead>
        <tr>
          <th>Sr. No.</th>
          <th>Link</th>
          <th>Checked At</th>
          <th>Running Status</th>
          <th>Error</th>
        </tr>
      </thead>
      <tbody>
        {logs.map((log, index) => (
          <tr key={log._id || index}>
            <td>{(currentPage - 1) * logsPerPage + (index + 1)}</td>
            <td className="link-cell">
              {log.link ? (
                <a href={log.link} target="_blank" rel="noopener noreferrer" title={log.link}>
                  {log.link.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]}
                </a>
              ) : (
                "-"
              )}
            </td>
            <td>{moment(log.checkedAt).format("DD MMM YYYY, hh:mm A")}</td>
            <td>
              {log.runningStatus ? (
                <span className="text-success fw-semibold">SUCCESS</span>
              ) : (
                <span className="text-danger fw-semibold">ERROR</span>
              )}
            </td>
            <td className={log.error ? "text-danger" : "text-success"}>
              {log.error ? (
                <span title={log.error}>{log.error}</span>
              ) : (
                "No Error"
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  useEffect(() => {
    reloadSite(id);
    fetchLogs(id);

    const interval = setInterval(() => {
      reloadSite(id);
      fetchLogs(id);
    }, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, [id]);

  const reloadSite = async (siteId) => {
    if (siteId) {
      const site = await getSiteById(siteId, showToast);
      setItems(site ? [site] : []);
    } else {
      const allSites = await getSiteData(showToast);
      setItems(Array.isArray(allSites) ? allSites : []);
    }
  };

  const fetchLogs = async (siteId, page = 1, perPage = logsPerPage) => {
    if (!siteId) return;
    const result = await getStateLogsById(siteId, page, perPage, showToast);

    setLogs(result?.logs || []);
    setCurrentPage(page);
    setTotalPages(Math.ceil((result?.totalLogs || 0) / perPage));
  };

  return (
    <div className="state-page px-2">
      <div className="px-2 sticky-header">
        <h3>{title} | States</h3>
        <AppMenu title="States" />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '5px 0' }}>
        <BackButton icon="←" />
        <h5 style={{ margin: 0 }}>Current Status</h5>
      </div>
      <StateList items={items} />

      {logs.length > 0 && (
        <>
          <h5 className="mt-4">Running Status Log History</h5>
          <div className="state-logs-wrapper" style={{ paddingBottom: "calc(40px + 2rem)" }}>
            <StateLogsTable
              logs={logs}
              currentPage={currentPage}
              logsPerPage={logsPerPage}
            />
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={logsPerPage}
            onPageChange={(page) => fetchLogs(id, page, logsPerPage)}
            onPageSizeChange={(size) => {
              setLogsPerPage(size);
              fetchLogs(id, 1, size);
            }}
          />
        </>
      )}
    </div>
  );
}

// Site Current Status Table Component
const StateList = ({ items }) => {
  const [localItems, setLocalItems] = useState(Array.isArray(items) ? [...items] : []);

  useEffect(() => {
    setLocalItems(Array.isArray(items) ? items : []);
  }, [items]);

  return (
    <Table className="table table-hover" responsive hover>
      <thead>
        <tr>
          <th>Name</th>
          <th>Link</th>
          <th>Server</th>
          <th>Running Status</th>
          <th>Site Status</th>
          <th>SSL Expiry</th>
          <th>Domain Expiry</th>
          <th>Last Checked</th>
          <th>Error Status</th>
        </tr>
      </thead>
      <tbody>
        {localItems.map((item, index) => (
          <tr key={item._id || index}>
            <td><strong>{item.name || '-'}</strong></td>
            <td className="link-cell">
              {item.link ? (
                <a href={item.link} target="_blank" rel="noopener noreferrer" title={item.link}>
                  {item.link.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]}
                </a>
              ) : (
                "-"
              )}
            </td>
            <td>{item.serverName || item.serverId || "-"}</td>
            <td>
              {item.runningStatus === true ? (
                <span className="text-success fw-semibold">SUCCESS</span>
              ) : item.runningStatus === false ? (
                <span className="text-danger fw-semibold">ERROR</span>
              ) : (
                '-'
              )}
            </td>
            <td>
              {item.status === true || item.active === 1 ? (
                <span className="badge bg-success">Active</span>
              ) : (
                <span className="badge bg-secondary">Inactive</span>
              )}
            </td>
            <td>
              {item.sslExpiry && item.sslExpiry !== 'NO_CERT_INFO'
                ? moment(item.sslExpiry).format("DD/MM/YYYY")
                : "-"}
            </td>
            <td>
              {item.domainExpiry
                ? moment(item.domainExpiry).format('DD/MM/YYYY')
                : '-'}
            </td>
            <td>
              {item.lastUpdated
                ? moment(item.lastUpdated).format("DD MMM YYYY, hh:mm A")
                : "-"}
            </td>
            <td className={item.lastError ? "text-danger" : "text-success"}>
              {item.lastError ? (
                <span title={item.lastError}>{item.lastError}</span>
              ) : "No Error"}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};
